import { styled, keyframes } from "styled-components";

import {
    BackgroundField,
    colorCSS,
    containerWidths,
    generateFieldsStyles,
    maxWidth,
    minWidth
} from "../../styles";
import { redundantCssFromKebab } from "../../styles/redundant-css";

const config = {
    bounceDuration: 4, //s
    columnGap: 16, //px
    columnWidth: 456, //px
    scrollDuration: 120 //s
};

const bounce = keyframes`
    from {
        box-shadow: -5px 5px 0 0 ${colorCSS("N900", 0.1)};
        transform: translate(0, 0);
    }
    to {
        box-shadow: -10px 10px 0 0 ${colorCSS("N900", 0.05)};
        transform: translate(10px, -10px);
    }
`;

const scroll = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(-50%);
    }
`;

export const Section = styled.section<{ background: BackgroundField[] }>`
    ${({ background }: { background: BackgroundField[] }) =>
        background && generateFieldsStyles(background)};
    ${redundantCssFromKebab("position", "relative")}

    .label {
        ${redundantCssFromKebab("animation-direction", "alternate")}
        ${redundantCssFromKebab(
            "animation-duration",
            `${config.bounceDuration}s`
        )}
        ${redundantCssFromKebab("animation-iteration-count", "infinite")}
        animation-name: ${bounce};
        ${redundantCssFromKebab("animation-timing-function", "ease-in-out")}

        &-0 {
            ${redundantCssFromKebab(
                "left",
                `calc((100% - ${containerWidths.lg}px) / 2 + 550px)`
            )}
            ${redundantCssFromKebab("top", "25%")}

            @media ${minWidth.xl} {
                ${redundantCssFromKebab(
                    "left",
                    `calc((100% - ${containerWidths.xl}px) / 2 + 650px)`
                )}
            }
        }

        &-1 {
            ${redundantCssFromKebab("animation-delay", "1s")}
            ${redundantCssFromKebab(
                "left",
                `calc((100% - ${containerWidths.lg}px) / 2 + 516px)`
            )}
            ${redundantCssFromKebab("top", "65%")}

            @media ${minWidth.xl} {
                ${redundantCssFromKebab(
                    "left",
                    `calc((100% - ${containerWidths.xl}px) / 2 + 616px)`
                )}
            }
        }

        &-2 {
            ${redundantCssFromKebab("animation-delay", "2s")}
            ${redundantCssFromKebab(
                "left",
                `calc((100% - ${containerWidths.lg}px) / 2 + 1040px)`
            )}
            ${redundantCssFromKebab("top", "15%")}

            @media ${minWidth.xl} {
                ${redundantCssFromKebab(
                    "left",
                    `calc((100% - ${containerWidths.xl}px) / 2 + 1156px)`
                )}
            }
        }

        &-3 {
            ${redundantCssFromKebab("animation-delay", "3s")}
            ${redundantCssFromKebab(
                "left",
                `calc((100% - ${containerWidths.lg}px) / 2 + 1010px)`
            )}
            ${redundantCssFromKebab("top", "50%")}

            @media ${minWidth.xl} {
                ${redundantCssFromKebab(
                    "left",
                    `calc((100% - ${containerWidths.xl}px) / 2 + 1126px)`
                )}
            }
        }

        @media (prefers-reduced-motion) {
            ${redundantCssFromKebab("animation-name", "none")}
        }

        @media ${maxWidth.lg} {
            ${redundantCssFromKebab("display", "none")}
        }
    }

    @media ${minWidth.lg} {
        ${redundantCssFromKebab("overflow", "hidden")}
        ${redundantCssFromKebab("padding-bottom", "7rem")}
    }

    @media ${minWidth.xl} {
        ${redundantCssFromKebab("padding-bottom", "9rem")}
    }
`;

export const MobileImages = styled.div`
    ${redundantCssFromKebab("margin", "2rem -1rem -5rem !important")}
    ${redundantCssFromKebab("padding", "0 1rem")}
    @media ${minWidth.lg} {
        ${redundantCssFromKebab("display", "none")}
    }
`;

//adds padding, especially on mobile
export const Wrapper = styled.div`
    ${redundantCssFromKebab("padding-bottom", "6rem")}
    @media ${minWidth.lg} {
        ${redundantCssFromKebab("padding-bottom", "1rem")}
    }
`;

const DesktopImageColumn = styled.div`
    animation-name: ${scroll};
    ${redundantCssFromKebab("display", "none")}
    ${redundantCssFromKebab("pointer-events", "none")}

    /* prevent mouse image 'lifting' */
    img {
        ${redundantCssFromKebab("margin-bottom", "1rem")}
        /* jitterFree way to create spacing between images */
        ${redundantCssFromKebab("transform", "translate3d(0, 0, 0)")}
    }

    @media ${minWidth.lg} {
        ${redundantCssFromKebab(
            "animation-duration",
            `${config.scrollDuration}s`
        )}
        ${redundantCssFromKebab("animation-iteration-count", "infinite")}
        ${redundantCssFromKebab("animation-timing-function", "linear")}
        ${redundantCssFromKebab("display", "flex")}
        left: calc(
            (100% - ${containerWidths.lg}px) / 2 +
                (${containerWidths.lg}px * 0.6)
        );
        ${redundantCssFromKebab("position", "absolute")}
        ${redundantCssFromKebab("top", "0")}
        ${redundantCssFromKebab("width", `${config.columnWidth}px`)}
    }

    @media ${minWidth.xl} {
        left: calc(
            (100% - ${containerWidths.xl}px) / 2 +
                (${containerWidths.xl}px * 0.6)
        );
    }

    @media (prefers-reduced-motion) {
        ${redundantCssFromKebab("animation-name", "none")}
    }
`;

export const DesktopImageColumnA = styled(DesktopImageColumn)({
    "flexDirection": "column",
    "flex-direction": "column"
});

export const DesktopImageColumnB = styled(DesktopImageColumn)({
    "animationDirection": "reverse",
    "animation-direction": "reverse",
    "flexDirection": "column",
    "flex-direction": "column",
    "marginLeft": `${config.columnWidth + config.columnGap}px`
});

export const Wave = styled.svg`
    ${redundantCssFromKebab("bottom", "-0.5px")}
    ${redundantCssFromKebab("fill", `${colorCSS("N0")}`)}
    ${redundantCssFromKebab("height", "auto")}
    ${redundantCssFromKebab("left", "0")}
    ${redundantCssFromKebab("max-height", "179px")}
    ${redundantCssFromKebab("position", "absolute")}
    ${redundantCssFromKebab("right", "0")}
    ${redundantCssFromKebab("width", "100%")}
`;

export const MobileWave = styled(Wave)({
    "display": "block",
    "@media (min-width: 992px)": {
        display: "none"
    }
});
export const DesktopWave = styled(Wave)({
    "display": "none",
    "@media (min-width: 992px)": {
        display: "block"
    }
});
